/**
 * CullBacklogModal Component
 *
 * Full-screen overlay that walks the user through reviewing stale backlog tasks.
 * Calls the AI backlog-cull endpoints to get recommendations, lets the user
 * override them, then applies the decisions in batches.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import clsx from 'clsx';
import {
  generateCullBatch,
  applyCullDecisions,
  type CullTask,
  type Decision,
} from '../api/backlogCull';

// ── Types ─────────────────────────────────────────────────────────────────────

type Phase = 'loading' | 'review' | 'empty' | 'error';

interface ReviewTask extends CullTask {
  userDecision: 'keep' | 'delete' | 'defer';
}

export interface CullBacklogModalProps {
  onClose: () => void;
  onComplete: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(isoString: string): string {
  return new Date(isoString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function dayLabel(n: number): string {
  return `${n} day${n === 1 ? '' : 's'}`;
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

// ── Component ─────────────────────────────────────────────────────────────────

export function CullBacklogModal({ onClose, onComplete }: CullBacklogModalProps) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [tasks, setTasks] = useState<ReviewTask[]>([]);
  const [totalStale, setTotalStale] = useState(0);
  const [batchRemaining, setBatchRemaining] = useState(0);
  const [batchesReviewed, setBatchesReviewed] = useState(0);
  const [isApplying, setIsApplying] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // ── Load first batch on mount ──────────────────────────────────────────────

  const loadBatch = useCallback(async () => {
    setPhase('loading');
    setErrorMessage(null);
    try {
      const res = await generateCullBatch();
      if (res.tasks.length === 0) {
        setPhase('empty');
        return;
      }
      setTasks(
        res.tasks.map((t) => ({
          ...t,
          userDecision: t.recommendation,
        })),
      );
      setTotalStale(res.totalStale);
      setBatchRemaining(res.batchRemaining);
      setPhase('review');
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to load batch');
      setPhase('error');
    }
  }, []);

  useEffect(() => {
    void loadBatch();
  }, [loadBatch]);

  // ── Decision toggle ────────────────────────────────────────────────────────

  function setDecision(taskId: string, decision: 'keep' | 'delete' | 'defer') {
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, userDecision: decision } : t)),
    );
  }

  // ── Apply decisions ────────────────────────────────────────────────────────

  async function handleApply() {
    setIsApplying(true);
    setErrorMessage(null);
    try {
      const decisions: Decision[] = tasks.map((t) => ({
        taskId: t.id,
        action: t.userDecision,
      }));
      await applyCullDecisions(decisions);
      setBatchesReviewed((n) => n + 1);

      if (batchRemaining <= 0) {
        onComplete();
        return;
      }
      await loadBatch();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to apply decisions');
    } finally {
      setIsApplying(false);
    }
  }

  // ── Skip batch (keep all, moves last_culled_at) ────────────────────────────

  async function handleSkip() {
    setIsApplying(true);
    setErrorMessage(null);
    try {
      const decisions: Decision[] = tasks.map((t) => ({
        taskId: t.id,
        action: 'keep' as const,
      }));
      await applyCullDecisions(decisions);
      setBatchesReviewed((n) => n + 1);

      if (batchRemaining <= 0) {
        onComplete();
        return;
      }
      await loadBatch();
    } catch (err) {
      setErrorMessage(err instanceof Error ? err.message : 'Failed to skip batch');
    } finally {
      setIsApplying(false);
    }
  }

  // ── Derived counts ─────────────────────────────────────────────────────────

  const deleteCount = tasks.filter((t) => t.userDecision === 'delete').length;
  const keepCount = tasks.filter((t) => t.userDecision === 'keep').length;
  const showCostWarning = batchesReviewed > 0 && batchRemaining > 0;
  const remainingBatches = Math.ceil(batchRemaining / 5);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="relative bg-surface-base-light dark:bg-surface-base-dark rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col mx-4">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle-light dark:border-border-subtle-dark">
          <div>
            <h2 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              Cull Your Backlog
            </h2>
            {phase === 'review' && totalStale > 0 && (
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark mt-0.5">
                {totalStale} task{totalStale === 1 ? '' : 's'} haven&apos;t been touched in 30+ days.
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark text-text-secondary-light dark:text-text-secondary-dark transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">

          {/* Loading */}
          {phase === 'loading' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <Loader2 className="w-8 h-8 text-primary-500 animate-spin" />
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                Analyzing your backlog…
              </p>
            </div>
          )}

          {/* Error */}
          {phase === 'error' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <AlertCircle className="w-8 h-8 text-red-500" />
              <p className="text-sm text-red-600 dark:text-red-400">
                {errorMessage ?? 'Something went wrong. Please try again.'}
              </p>
              <button
                onClick={() => void loadBatch()}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                Try again
              </button>
            </div>
          )}

          {/* Empty */}
          {phase === 'empty' && (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-center">
              <CheckCircle2 className="w-10 h-10 text-green-500 dark:text-green-400" />
              <p className="font-medium text-text-primary-light dark:text-text-primary-dark">
                Your backlog is clean!
              </p>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                No tasks older than 30 days.
              </p>
            </div>
          )}

          {/* Review */}
          {phase === 'review' && tasks.map((task) => (
            <div
              key={task.id}
              className="border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-4 space-y-2"
            >
              {/* Title */}
              <p className="font-semibold text-text-primary-light dark:text-text-primary-dark leading-snug">
                {task.title}
              </p>

              {/* Meta */}
              <p className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark">
                Created {formatDate(task.createdAt)} &middot; No activity for {dayLabel(task.daysSinceActivity)}
              </p>

              {/* AI suggestion + reasoning */}
              <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                <span className="font-medium">AI suggests: {capitalize(task.recommendation)}</span>
                {' — '}
                <span className="italic">&ldquo;{task.reasoning}&rdquo;</span>
              </p>

              {/* Decision buttons */}
              <div className="flex items-center gap-2 pt-1">
                {(['keep', 'defer', 'delete'] as const).map((action) => {
                  const isSelected = task.userDecision === action;
                  const isDefer = action === 'defer';
                  return (
                    <button
                      key={action}
                      onClick={() => !isDefer && setDecision(task.id, action)}
                      disabled={isDefer}
                      title={isDefer ? 'Snooze feature coming soon' : undefined}
                      className={clsx(
                        'px-3 py-1.5 text-xs rounded-full transition-colors',
                        isSelected
                          ? 'bg-primary-100 dark:bg-primary-900/40 text-primary-700 dark:text-primary-300 font-medium ring-1 ring-primary-400 dark:ring-primary-600'
                          : 'bg-surface-hover-light dark:bg-surface-hover-dark text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-base-dark/10',
                        isDefer && 'opacity-50 cursor-not-allowed',
                      )}
                    >
                      {capitalize(action)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

        </div>

        {/* Footer */}
        {(phase === 'review' || phase === 'error') && phase !== 'error' && (
          <div className="px-6 py-4 border-t border-border-subtle-light dark:border-border-subtle-dark space-y-3">
            {/* Cost warning banner */}
            {showCostWarning && (
              <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/40 rounded-lg text-xs text-amber-700 dark:text-amber-300">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  ~{remainingBatches} AI call{remainingBatches === 1 ? '' : 's'} needed to review all remaining tasks.
                </span>
              </div>
            )}

            {/* Inline apply error */}
            {errorMessage && (
              <p className="text-xs text-red-600 dark:text-red-400">{errorMessage}</p>
            )}

            <div className="flex items-center justify-between gap-3">
              <button
                onClick={() => void handleApply()}
                disabled={isApplying}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isApplying ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Applying…
                  </>
                ) : (
                  `Apply decisions (${deleteCount} delete, ${keepCount} keep)`
                )}
              </button>
              <button
                onClick={() => void handleSkip()}
                disabled={isApplying}
                className="px-4 py-2 text-text-secondary-light dark:text-text-secondary-dark hover:bg-surface-hover-light dark:hover:bg-surface-hover-dark text-sm rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Skip batch
              </button>
            </div>
          </div>
        )}

        {/* Footer for empty phase — just a close button */}
        {phase === 'empty' && (
          <div className="px-6 py-4 border-t border-border-subtle-light dark:border-border-subtle-dark flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
